import React, { useCallback, useContext } from "react";
import { UI } from "@wwimmo/ui";

const ListRow = UI.List.Row as React.FC<React.PropsWithChildren<{ className?: string }>>;
import { RootStoreContext } from "src/stores/RootStore";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { IAssemblyAttendances, IAttendanceState } from "src/stores/assemblies/AttendanceListStore";
import styles from "./AttendanceList.module.css";

const formatValueQuota = (value: string | null): string => {
    if (!value) {
        return "0.0";
    }
    const formatted = parseFloat(value).toFixed(4).replace(/0+$/, "");
    return formatted.endsWith(".") ? `${formatted}0` : formatted;
};

const AttendanceListRowDesktopBase = (props: {
    attendances: IAssemblyAttendances;
    widths: number[];
    showTenantName: boolean;
}) => {
    const { attendanceListStore, assemblyStore } = useContext(RootStoreContext);
    const { attendancestate, tenantname, unitid, unitname, value, tenantid, isunitchecked, istenantchecked } =
        props.attendances;
    const { widths, showTenantName } = props;
    const isReadOnly = assemblyStore.isAssemblyReadOnly;

    const onPresentCheckboxChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            // If not checked -> PRESENT, if already checked -> ABSENT
            const targetKey = e.target.checked ? IAttendanceState.PRESENT : IAttendanceState.ABSENT;
            const stateId = attendanceListStore.attendanceStates.find((s) => s.key === targetKey)?.id;
            if (stateId) {
                attendanceListStore.updateAttendanceState(tenantid, stateId);
            }
        },
        [tenantid, attendanceListStore]
    );

    const onRepresentedCheckboxChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            // If not checked -> REPRESENTED, if already checked -> PRESENT
            const targetKey = e.target.checked ? IAttendanceState.REPRESENTED : IAttendanceState.PRESENT;
            const stateId = attendanceListStore.attendanceStates.find((s) => s.key === targetKey)?.id;
            if (stateId) {
                attendanceListStore.updateAttendanceState(tenantid, stateId);
            }
        },
        [tenantid, attendanceListStore]
    );

    if (!attendanceListStore.isListViewPresent) {
        return (
            <ListRow>
                <UI.List.Cell
                    colspan={0.2}
                    className={styles.CheckboxState}
                    value={
                        showTenantName ? (
                            <UI.Checkbox
                                className={styles.CheckboxState}
                                name={`attendance-tenantchecked-${tenantid}`}
                                checked={!!istenantchecked}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    attendanceListStore.updateTenantChecked(tenantid, e.target.checked)
                                }
                                disabled={isReadOnly}
                            />
                        ) : (
                            ""
                        )
                    }
                />
                <UI.List.Cell value={showTenantName ? tenantname : ""} colspan={0.6} />
                <UI.List.Cell colspan={0.3} value={formatValueQuota(value)} />
                <UI.List.Cell
                    colspan={0.2}
                    className={styles.CheckboxState}
                    value={
                        <UI.Checkbox
                            className={styles.CheckboxState}
                            name={`attendance-unitchecked-${tenantid}`}
                            checked={!!isunitchecked}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                attendanceListStore.updateUnitChecked(unitid, e.target.checked)
                            }
                            disabled={isReadOnly}
                        />
                    }
                />
                <UI.List.Cell value={unitname} colspan={widths[2]} />
            </ListRow>
        );
    }

    return (
        <ListRow>
            <UI.List.Cell value={showTenantName ? tenantname : ""} colspan={widths[0]} />
            <UI.List.Cell colspan={widths[1]} value={formatValueQuota(value)} />
            <UI.List.Cell value={unitname} colspan={widths[2]} />
            <UI.List.Cell
                colspan={widths[3]}
                value={
                    showTenantName ? (
                        <UI.Checkbox
                            className={styles.CheckboxState}
                            name={`attendance-present-${tenantid}`}
                            checked={
                                attendancestate === IAttendanceState.PRESENT ||
                                attendancestate === IAttendanceState.REPRESENTED
                            }
                            onChange={onPresentCheckboxChange}
                            disabled={isReadOnly}
                        />
                    ) : (
                        ""
                    )
                }
            />
            <UI.List.Cell
                colspan={widths[4]}
                value={
                    showTenantName ? (
                        <UI.Checkbox
                            className={styles.CheckboxState}
                            name={`attendance-represented-${tenantid}`}
                            checked={attendancestate === IAttendanceState.REPRESENTED}
                            onChange={onRepresentedCheckboxChange}
                            disabled={isReadOnly}
                        />
                    ) : (
                        ""
                    )
                }
            />
        </ListRow>
    );
};

export const AttendanceListRowDesktop = Sentry.withProfiler(observer(AttendanceListRowDesktopBase));
